import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { motion } from "framer-motion";
import { Header } from "components/shared";
import { useAuth } from "utils/context";
import { DashboardStats, ROLES } from "utils";
import { format, subDays } from "date-fns";
import { getDashboardStats } from "utils/api/private/DashboardApi";
import { useUtility } from "utils/context/UtilityContext";

const KpiCard = ({ title, value, format = "currency" }: { title: string; value: number | null | undefined; format?: "currency" | "number" | "percent" }) => {
  // Use `(value ?? 0)` to default null or undefined values to 0 before calling toFixed()
  const formattedValue = format === "currency"
    ? `$${(value ?? 0).toFixed(2)}`
    : format === "percent"
    ? `${(value ?? 0).toFixed(2)}%`
    : (value ?? 0).toLocaleString(); 
    
  return (
    <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
      <Typography variant="h6" color="text.secondary">{title}</Typography>
      <Typography variant="h4" fontWeight="bold" color="primary">{formattedValue}</Typography>
    </Paper>
  );
};

export const DashboardAdmin = () => {
  const { marginBottomHeader } = useUtility();
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMIN;

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Draft state for UI controls
  const [draftPeriod, setDraftPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [draftStartDate, setDraftStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [draftEndDate, setDraftEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [draftCountryCode, setDraftCountryCode] = useState<string>("");

  // Applied state for API calls
  const [appliedFilters, setAppliedFilters] = useState({
    period: draftPeriod,
    startDate: draftStartDate,
    endDate: draftEndDate,
    countryCode: draftCountryCode,
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboardStats(appliedFilters);
      setStats(data);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      period: draftPeriod,
      startDate: draftStartDate,
      endDate: draftEndDate,
      countryCode: draftCountryCode,
    });
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`${marginBottomHeader} flex flex-col items-center justify-start gap-4 p-4`}
    >
      <Header secondPart={false} />
      <Box sx={{ width: '100%', p: { xs: 1, sm: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>Dashboard</Typography>

        <Paper elevation={2} sx={{ p: 2, mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={isAdmin ? 2.4 : 3}><FormControl fullWidth><InputLabel>Period</InputLabel><Select value={draftPeriod} label="Period" onChange={(e) => setDraftPeriod(e.target.value as any)}><MenuItem value="week">Weekly</MenuItem><MenuItem value="month">Monthly</MenuItem><MenuItem value="year">Yearly</MenuItem></Select></FormControl></Grid>
            <Grid item xs={12} sm={6} md={isAdmin ? 2.4 : 3}><TextField fullWidth label="Start Date" type="date" value={draftStartDate} onChange={(e) => setDraftStartDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={6} md={isAdmin ? 2.4 : 3}><TextField fullWidth label="End Date" type="date" value={draftEndDate} onChange={(e) => setDraftEndDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
            {isAdmin && (<Grid item xs={12} sm={6} md={2.4}><TextField fullWidth label="Filter by Country" variant="outlined" value={draftCountryCode} onChange={(e) => setDraftCountryCode(e.target.value)} placeholder="e.g., US, FR, CD" /></Grid>)}
            <Grid item xs={12} sm={12} md={isAdmin ? 2.4 : 3}><Button fullWidth variant="contained" onClick={handleApplyFilters} sx={{ height: '56px' }}>Apply Filters</Button></Grid>
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {stats && (
          <Grid container spacing={4}>
            {/* KPIs */}
            <Grid item xs={12} sm={6} md={2.4}><KpiCard title="Total Revenue" value={stats.kpis.totalRevenue} /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><KpiCard title="Total Cost" value={stats.kpis.totalCost} /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><KpiCard title="Gross Profit" value={stats.kpis.grossProfit} /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><KpiCard title="Profit Margin" value={stats.kpis.profitMargin} format="percent" /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><KpiCard title="Total Sales" value={stats.kpis.totalSales} format="number" /></Grid>

            {/* Revenue Over Time */}
            <Grid item xs={12}><Paper elevation={3} sx={{ p: 2 }}><Typography variant="h6" gutterBottom>Revenue, Cost, and Profit</Typography><Box sx={{ height: 400 }}><LineChart xAxis={[{ scaleType: 'point', data: stats.revenueOverTime.map(d => d.date) }]} series={[{ data: stats.revenueOverTime.map(d => d.revenue), label: 'Revenue', color: '#2e7d32' },{ data: stats.revenueOverTime.map(d => d.cost), label: 'Cost', color: '#d32f2f' },{ data: stats.revenueOverTime.map(d => d.profit), label: 'Profit', color: '#1976d2' }]} /></Box></Paper></Grid>

            {/* Top Products & Buyers */}
            <Grid item xs={12} md={8}><Paper elevation={3} sx={{ p: 2, height: '100%' }}><Typography variant="h6" gutterBottom>Top 10 Products by Revenue</Typography><Box sx={{ height: 400 }}><BarChart dataset={stats.topPerformingProducts.map(p => ({ name: p.name, totalRevenue: p.totalRevenue }))} yAxis={[{ scaleType: 'band', dataKey: 'name' }]} series={[{ dataKey: 'totalRevenue', label: 'Revenue (USD)' }]} layout="horizontal" /></Box></Paper></Grid>
            <Grid item xs={12} md={4}><Paper elevation={3} sx={{ p: 2, height: '100%' }}><Typography variant="h6" gutterBottom>Top 5 Buyers</Typography><TableContainer><Table><TableHead><TableRow><TableCell>Username</TableCell><TableCell align="right">Total Spent</TableCell></TableRow></TableHead><TableBody>{stats.topBuyers.map(buyer => (<TableRow key={buyer.userId}><TableCell>{buyer.username}</TableCell><TableCell align="right">${buyer.totalSpent.toFixed(2)}</TableCell></TableRow>))}</TableBody></Table></TableContainer></Paper></Grid>

            {/* Sales by Country */}
            <Grid item xs={12}><Paper elevation={3} sx={{ p: 2 }}><Typography variant="h6" gutterBottom>Sales Revenue by Country</Typography><Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><PieChart series={[{ data: stats.salesByCountry.map((c, i) => ({ id: i, value: c.revenue, label: `${c.country} ($${c.revenue.toFixed(2)})` })) }]} /></Box></Paper></Grid>
          </Grid>
        )}
      </Box>
    </motion.div>
  );
};

